import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, LogIn, UserPlus, Activity, Eye, EyeOff, ShieldCheck, Flame } from "lucide-react";

const inputCls =
  "w-full bg-black border border-[#1A1A24] px-4 py-3 text-sm text-[#E2E8F0] placeholder:text-[#3a3f4a] focus:outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]/40 transition-all duration-200 font-mono";

export default function LoginPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      if (mode === "register") {
        await register(email, password);
        setSuccess("Account created! Logging you in…");
        await login(email, password);
      } else {
        await login(email, password);
      }
    } catch (err) {
      setError(err?.message || "Invalid credentials or sign up error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] grid-bg flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Ambient glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full bg-[#00F0FF]/5 blur-[100px] pointer-events-none" />

      {/* Logo */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8 text-center relative z-10"
      >
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="w-14 h-14 rounded-xl overflow-hidden border border-[#00F0FF]/60 glow-cyan p-0.5 bg-[#050505] flex items-center justify-center flex-shrink-0">
            <img src="/logo.png" alt="Mwangi Trades Logo" className="w-full h-full object-cover rounded-lg" />
          </div>
          <div className="leading-none text-left">
            <div className="font-display font-black text-2xl sm:text-3xl tracking-tight text-white">
              MWANGI<span className="text-[#00F0FF] text-glow-cyan">TRADES</span>
            </div>
            <div className="text-[10px] text-[#00F0FF] tracking-[0.25em] uppercase font-mono mt-1">ICC MAFIA JOURNAL</div>
          </div>
        </div>
        <div className="text-[11px] text-[#848D97] tracking-[0.35em] uppercase">
          Indication · Correction · Continuation · Trades by Mwangi
        </div>
      </motion.div>

      {/* Inspiration Card */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.05 }}
        className="w-full max-w-md mb-6 border border-[#00F0FF]/30 bg-[#0C0C0E]/90 p-5 backdrop-blur-xl glow-cyan relative z-10 overflow-hidden"
      >
        <div className="flex items-center gap-2 mb-2 text-[#00F0FF]">
          <Flame className="w-4 h-4 text-[#00F0FF]" />
          <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#00F0FF]">
            MINDSET OF EXCELLENCE
          </span>
        </div>
        <blockquote className="text-xs text-[#E2E8F0] font-mono leading-relaxed italic border-l-2 border-[#00F0FF] pl-3 py-1">
          "DISCIPLINE IS THE BRIDGE BETWEEN INTENTION AND EXECUTION. Respect your Indication, master your Correction, execute your Continuation — wealth is the byproduct of mechanical consistency."
        </blockquote>
        <div className="text-right text-[10px] text-[#00F0FF] font-bold tracking-widest uppercase mt-2">
          — TRADER MWANGI
        </div>
      </motion.div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, delay: 0.1 }}
        className="w-full max-w-md border border-[#1A1A24] bg-[#0A0A0E]/90 backdrop-blur-xl relative z-10"
      >
        {/* Card header */}
        <div className="flex border-b border-[#1A1A24]">
          <>
              <button
                onClick={() => { setMode("login"); setError(""); }}
                className={`flex-1 flex items-center justify-center gap-2 py-4 text-xs tracking-[0.2em] uppercase transition-colors ${
                  mode === "login"
                    ? "text-[#00F0FF] border-b-2 border-[#00F0FF]"
                    : "text-[#848D97] hover:text-white"
                }`}
              >
                <LogIn className="w-3.5 h-3.5" /> Sign In
              </button>
              <button
                onClick={() => { setMode("register"); setError(""); }}
                className={`flex-1 flex items-center justify-center gap-2 py-4 text-xs tracking-[0.2em] uppercase transition-colors ${
                  mode === "register"
                    ? "text-[#00F0FF] border-b-2 border-[#00F0FF]"
                    : "text-[#848D97] hover:text-white"
                }`}
              >
                <UserPlus className="w-3.5 h-3.5" /> Register
              </button>
            </>
        </div>

        <div className="p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
            >

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-[11px] tracking-[0.15em] uppercase text-[#848D97] mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="trader@example.com"
                    required
                    autoFocus
                    className={inputCls}
                    data-testid="login-email"
                  />
                </div>

                <div>
                  <label className="block text-[11px] tracking-[0.15em] uppercase text-[#848D97] mb-1.5">
                    Password {mode === "register" && <span className="text-[#3a3f4a]">(min 8 chars)</span>}
                  </label>
                  <div className="relative">
                    <input
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••••••"
                      required
                      minLength={mode === "register" ? 8 : undefined}
                      className={`${inputCls} pr-11`}
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#848D97] hover:text-[#00F0FF] transition-colors"
                      tabIndex={-1}
                    >
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Error */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="border border-[#FF003C]/40 bg-[#FF003C]/5 px-4 py-2.5 text-xs text-[#FF003C]"
                      data-testid="login-error"
                    >
                      {error}
                    </motion.div>
                  )}
                  {success && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="border border-[#00FF41]/40 bg-[#00FF41]/5 px-4 py-2.5 text-xs text-[#00FF41]"
                    >
                      {success}
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.button
                  whileTap={{ scale: 0.97 }}
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit"
                  className="w-full flex items-center justify-center gap-2 py-3 bg-[#00F0FF] text-black font-bold text-sm uppercase tracking-wider glow-cyan disabled:opacity-60 transition-opacity mt-2"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : mode === "login" ? (
                    <LogIn className="w-4 h-4" />
                  ) : (
                    <UserPlus className="w-4 h-4" />
                  )}
                  {loading ? "Authenticating..." : mode === "login" ? "Sign In" : "Create Account"}
                </motion.button>
              </form>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-[#1A1A24] flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-[#00FF41] animate-pulse" />
          <span className="text-[10px] text-[#848D97] tracking-wider">
            PROTECTED BY JWT · HTTPONLY COOKIE · BCRYPT
          </span>
        </div>
      </motion.div>
    </div>
  );
}
